import { useState, useEffect } from 'react';
import {
  createInvitation,
  deleteInvitation,
  resendInvitation,
} from '../services/invitationService';
import {
  getStaffUsers as fetchStaffUsers,
  updateStaffUserStatus,
} from '../services/userService';
import authService from '../services/authService';
import ConfirmDialog from '../components/ConfirmDialog';
import '../pages/Invitations.css';

const StaffUsers = () => {
  // Combined state for staff users and invitations
  const [staffList, setStaffList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<any>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger',
  });

  useEffect(() => {
    loadStaffList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadStaffList = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all staff users (including pending invitations)
      const usersResponse = await fetchStaffUsers({});
      const users = usersResponse.data.users || [];

      // Apply status filter
      const filteredList = statusFilter
        ? users.filter((user: any) => user.status === statusFilter)
        : users;

      // Sort by creation date (newest first)
      filteredList.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setStaffList(filteredList);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load staff list');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      await createInvitation(formData);
      setCreateSuccess('Invitation sent successfully!');
      setFormData({ firstName: '', lastName: '', email: '' });
      setShowModal(false);
      loadStaffList();
      setTimeout(() => setCreateSuccess(null), 5000);
    } catch (err: any) {
      setCreateError(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to create invitation');
    } finally {
      setCreating(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ firstName: '', lastName: '', email: '' });
    setCreateError(null);
  };

  const handleDeleteInvitation = (userId: any) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke Invitation',
      message: 'Are you sure you want to revoke this invitation? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          await deleteInvitation(userId);
          loadStaffList();
        } catch (err: any) {
          setError(err.response?.data?.error?.message || 'Failed to revoke invitation');
        }
      },
    });
  };

  const handleResendInvitation = async (userId: any) => {
    try {
      await resendInvitation(userId);
      setCreateSuccess('Invitation email resent successfully!');
      loadStaffList();
      setTimeout(() => setCreateSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to resend invitation');
    }
  };

  const handleToggleUserStatus = (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';

    // Prevent users from deactivating themselves
    const currentUser = authService.getStoredUser();
    if (currentUser && currentUser.id === userId && newStatus === 'inactive') {
      const closeDialog = () => setConfirmDialog({ ...confirmDialog, isOpen: false });
      setConfirmDialog({
        isOpen: true,
        title: 'Cannot Deactivate Self',
        message: 'You cannot deactivate your own account. Please ask another administrator to deactivate your account if needed.',
        type: 'danger',
        onConfirm: closeDialog,
        onCancel: closeDialog,
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} this user? ${newStatus === 'inactive' ? 'They will no longer be able to access the system.' : 'They will regain access to the system.'}`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          await updateStaffUserStatus(userId, newStatus);
          loadStaffList();
        } catch (err) {
          setError(err.response?.data?.error?.message || `Failed to ${action} user`);
        }
      },
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'badge-pending';
      case 'active':
        return 'badge-accepted';
      case 'inactive':
        return 'badge-expired';
      default:
        return '';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="invitations-page">
      <div className="invitations-header">
        <div>
          <h1>Staff Users</h1>
          <p className="subtitle">Manage staff members and send invitations to new admins</p>
        </div>

        {/* Create New User Button */}
        <div className="header-actions">
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            Create New User
          </button>
        </div>
      </div>

      {/* Status messages for invitation */}
      {createSuccess && (
        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-success">{createSuccess}</div>
        </div>
      )}

      {/* Create User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Staff User</h2>
              <button className="modal-close" onClick={handleModalClose}>&times;</button>
            </div>
            {createError && (
              <div className="modal-alert alert-error">{createError}</div>
            )}
            <form onSubmit={handleCreateInvitation} className="modal-form">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Enter first name"
                  required
                  disabled={creating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Enter last name"
                  required
                  disabled={creating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  dir="ltr"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                  disabled={creating}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn btn-secondary"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Staff Members List */}
      <div className="card">
        <div className="card-header">
          <div className="filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {loading && <div className="loading">Loading staff members...</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {!loading && !error && staffList.length === 0 && (
          <div className="empty-state">
            <p>No staff members found.</p>
          </div>
        )}

        {!loading && !error && staffList.length > 0 && (
          <div className="table-container">
            <table className="invitations-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.fullName || '-'}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      {user.status === 'pending'
                        ? '-'
                        : user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : 'Never'}
                    </td>
                    <td>
                      {user.status === 'pending' ? (
                        <>
                          <button
                            onClick={() => handleResendInvitation(user.id)}
                            className="btn btn-primary btn-sm"
                            style={{ marginRight: '8px' }}
                          >
                            Resend invitation
                          </button>
                          <button
                            onClick={() => handleDeleteInvitation(user.id)}
                            className="btn btn-danger btn-sm"
                          >
                            Revoke
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.status)}
                          className={`btn btn-sm ${user.status === 'active' ? 'btn-danger' : 'btn-primary'}`}
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        type={confirmDialog.type}
      />
    </div>
  );
};

export default StaffUsers;
